/**
 * StripePaymentForm - Direct card entry using Stripe Elements
 * Uses separate fields for card number, expiry, CVC, and ZIP
 */
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Loader2, Lock, CheckCircle, AlertCircle, Calendar, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styling for individual Stripe elements to match dark theme
const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      color: '#22c55e',
      iconColor: '#22c55e',
    },
  },
};

// Inner form component that uses Stripe hooks
const CheckoutForm = ({ 
  amount, 
  description, 
  customerEmail,
  customerName,
  customerPhone,
  metadata = {},
  onSuccess, 
  onError,
  disabled = false,
  termsAccepted = true,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  
  // Track completion status of each field
  const [fieldStatus, setFieldStatus] = useState({
    cardNumber: { complete: false, error: null },
    cardExpiry: { complete: false, error: null },
    cardCvc: { complete: false, error: null },
  });

  // Check if all card fields are complete
  const allFieldsComplete = 
    fieldStatus.cardNumber.complete && 
    fieldStatus.cardExpiry.complete && 
    fieldStatus.cardCvc.complete &&
    postalCode.length >= 5;

  // Check for any errors
  const hasError = 
    fieldStatus.cardNumber.error || 
    fieldStatus.cardExpiry.error || 
    fieldStatus.cardCvc.error;

  const handleFieldChange = (field) => (event) => {
    setFieldStatus(prev => ({
      ...prev,
      [field]: {
        complete: event.complete,
        error: event.error ? event.error.message : null,
      }
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions');
      return;
    }

    if (!allFieldsComplete) {
      toast.error('Please complete all card details');
      return;
    }

    // Guard against $0 or invalid amounts
    if (!amount || amount <= 0) {
      toast.error('Invalid payment amount. Please refresh and try again.');
      return;
    }

    // Round to 2 decimal places to avoid floating point issues
    const chargeAmount = Math.round(amount * 100) / 100;

    setProcessing(true);

    try {
      // Log the exact amount being sent to Stripe
      console.log('💳 StripePaymentForm - Sending to Stripe:', {
        originalAmount: amount,
        chargeAmount: chargeAmount,
        chargeAmountFixed: chargeAmount.toFixed(2),
        description: description,
        customerEmail: customerEmail,
        metadata: metadata
      });
      
      // Create PaymentIntent on backend with validated amount
      const intentResponse = await axios.post(`${API}/payments/create-intent`, {
        amount: chargeAmount,
        description: description || 'Payment',
        customer_email: customerEmail,
        customer_name: customerName,
        customer_phone: customerPhone,
        metadata,
      });
      
      console.log('💳 PaymentIntent created:', intentResponse.data);

      if (!intentResponse.data.success) {
        throw new Error(intentResponse.data.error || 'Failed to create payment');
      }

      const { client_secret, payment_intent_id } = intentResponse.data;

      // Get the card number element for payment confirmation
      const cardNumberElement = elements.getElement(CardNumberElement);

      // Confirm the payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: customerName || undefined,
            email: customerEmail || undefined,
            phone: customerPhone || undefined,
            address: {
              postal_code: postalCode,
            },
          },
        },
      });

      if (error) {
        console.error('Stripe payment error:', error);
        
        // Parse error for user-friendly message
        let errorMessage = error.message || 'Payment failed. Please try again.';
        if (error.code === 'card_declined') {
          errorMessage = 'Your card was declined. Please try a different card.';
        } else if (error.code === 'expired_card') {
          errorMessage = 'Your card has expired. Please use a different card.';
        } else if (error.code === 'incorrect_cvc') {
          errorMessage = 'Invalid CVC code. Please check and try again.';
        } else if (error.code === 'insufficient_funds') {
          errorMessage = 'Insufficient funds. Please try a different card.';
        } else if (error.code === 'incorrect_zip') {
          errorMessage = 'Invalid ZIP code. Please check and try again.';
        }
        
        if (onError) onError(new Error(errorMessage));
        toast.error(errorMessage);
      } else if (paymentIntent.status === 'succeeded') {
        if (onSuccess) {
          onSuccess({
            paymentIntentId: paymentIntent.id,
            status: paymentIntent.status,
          });
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Payment failed';
      if (onError) onError(new Error(errorMessage));
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Card Number */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-200 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-slate-400" />
          Card Number
        </Label>
        <div className={`p-3 bg-slate-700 border rounded-lg transition-colors ${
          fieldStatus.cardNumber.error 
            ? 'border-red-500' 
            : fieldStatus.cardNumber.complete 
              ? 'border-green-500' 
              : 'border-slate-500 focus-within:border-teal-500'
        }`}>
          <CardNumberElement 
            options={ELEMENT_OPTIONS} 
            onChange={handleFieldChange('cardNumber')}
          />
        </div>
        {fieldStatus.cardNumber.error && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {fieldStatus.cardNumber.error}
          </p>
        )}
      </div>

      {/* Expiry and CVC Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Expiry Date */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            Expiry Date
          </Label>
          <div className={`p-3 bg-slate-700 border rounded-lg transition-colors ${
            fieldStatus.cardExpiry.error 
              ? 'border-red-500' 
              : fieldStatus.cardExpiry.complete 
                ? 'border-green-500' 
                : 'border-slate-500 focus-within:border-teal-500'
          }`}>
            <CardExpiryElement 
              options={ELEMENT_OPTIONS} 
              onChange={handleFieldChange('cardExpiry')}
            />
          </div>
          {fieldStatus.cardExpiry.error && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {fieldStatus.cardExpiry.error}
            </p>
          )}
        </div>

        {/* CVC */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-slate-400" />
            CVC
          </Label>
          <div className={`p-3 bg-slate-700 border rounded-lg transition-colors ${
            fieldStatus.cardCvc.error 
              ? 'border-red-500' 
              : fieldStatus.cardCvc.complete 
                ? 'border-green-500' 
                : 'border-slate-500 focus-within:border-teal-500'
          }`}>
            <CardCvcElement 
              options={ELEMENT_OPTIONS} 
              onChange={handleFieldChange('cardCvc')}
            />
          </div>
          {fieldStatus.cardCvc.error && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {fieldStatus.cardCvc.error}
            </p>
          )}
        </div>
      </div>

      {/* ZIP Code - Regular input since Stripe doesn't have a separate ZIP element */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-200">
          ZIP / Postal Code
        </Label>
        <Input
          type="text"
          placeholder="12345"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
          className={`bg-slate-700 border text-white placeholder:text-slate-500 ${
            postalCode.length >= 5 ? 'border-green-500' : 'border-slate-500'
          }`}
          maxLength={10}
          data-testid="postal-code-input"
        />
      </div>

      {/* Field Completion Status */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className={`flex items-center gap-1 px-2 py-1 rounded ${
          fieldStatus.cardNumber.complete ? 'bg-green-900/30 text-green-400' : 'bg-slate-700 text-slate-400'
        }`}>
          {fieldStatus.cardNumber.complete ? <CheckCircle className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
          Card
        </span>
        <span className={`flex items-center gap-1 px-2 py-1 rounded ${
          fieldStatus.cardExpiry.complete ? 'bg-green-900/30 text-green-400' : 'bg-slate-700 text-slate-400'
        }`}>
          {fieldStatus.cardExpiry.complete ? <CheckCircle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
          Expiry
        </span>
        <span className={`flex items-center gap-1 px-2 py-1 rounded ${
          fieldStatus.cardCvc.complete ? 'bg-green-900/30 text-green-400' : 'bg-slate-700 text-slate-400'
        }`}>
          {fieldStatus.cardCvc.complete ? <CheckCircle className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
          CVC
        </span>
        <span className={`flex items-center gap-1 px-2 py-1 rounded ${
          postalCode.length >= 5 ? 'bg-green-900/30 text-green-400' : 'bg-slate-700 text-slate-400'
        }`}>
          {postalCode.length >= 5 ? <CheckCircle className="h-3 w-3" /> : <span className="h-3 w-3 text-center">#</span>}
          ZIP
        </span>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || processing || disabled || !allFieldsComplete || !termsAccepted || !amount || amount <= 0}
        className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="stripe-pay-button"
      >
        {processing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Processing...
          </>
        ) : !amount || amount <= 0 ? (
          <>
            <AlertCircle className="h-4 w-4" />
            Invalid Amount
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" />
            Pay ${amount.toFixed(2)}
          </>
        )}
      </Button>

      {/* Requirements helper */}
      {!processing && amount > 0 && !termsAccepted && (
        <p className="text-xs text-amber-400 text-center">
          Please accept the Terms and Conditions above
        </p>
      )}

      {/* Security Notice */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
        <Lock className="h-3 w-3" />
        <span>Your payment is secured by Stripe</span>
      </div>
    </form>
  );
};

// Main component that wraps with Stripe Elements provider
const StripePaymentForm = (props) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initStripe = async () => {
      try {
        const response = await axios.get(`${API}/stripe/config`);
        if (response.data?.publishable_key) {
          const stripe = loadStripe(response.data.publishable_key);
          setStripePromise(stripe);
        } else {
          throw new Error('No Stripe publishable key found');
        }
      } catch (err) {
        console.error('Failed to initialize Stripe:', err);
        setError('Failed to load payment form. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    initStripe();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading payment form...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
        <p className="text-amber-400">Payment system unavailable</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm {...props} />
    </Elements>
  );
};

export default StripePaymentForm;
